// src/components/cooperativas.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Container, Row, Col, ButtonGroup, Button,
  Card, Modal, ListGroup, Spinner,
} from "react-bootstrap";
import "../components/cooperativas.css";

const API_URL = "https://facach.coop.hn/api";
const CMS_URL = "https://facach.coop.hn";

const phoneToTel = (str = "") => str.replace(/[^0-9+]/g, "");

const buildImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${CMS_URL}${path}`;
};

// ─── Render de sedes ──────────────────────────────────────────────────────────
const RenderSedes = ({ branches = [] }) => {
  if (!branches.length) {
    return <div className="text-muted">Sin sedes registradas.</div>;
  }
  return (
    <ListGroup variant="flush" className="sedes-list">
      {branches.map((s, i) => (
        <ListGroup.Item key={i} className="px-0">
          <div className="sede-item">
            <div className="sede-titulo"><strong>{s.name || "Sede"}</strong></div>

            {s.address && (
              <div className="sede-linea">
                <span className="sede-label">Dirección:</span> <span>{s.address}</span>
              </div>
            )}
            {s.phone && (
              <div className="sede-linea">
                <span className="sede-label">Teléfono:</span>{" "}
                <a href={`tel:${phoneToTel(s.phone)}`}>{s.phone}</a>
              </div>
            )}
            {s.email && (
              <div className="sede-linea">
                <span className="sede-label">Correo:</span>{" "}
                <a href={`mailto:${s.email}`}>{s.email}</a>
              </div>
            )}
            {s.whatsapp && (
              <div className="sede-linea">
                <span className="sede-label">WhatsApp:</span>{" "}
                <a href={`https://wa.me/${phoneToTel(s.whatsapp)}`}
                  target="_blank" rel="noreferrer">{s.whatsapp}</a>
              </div>
            )}
            {s.schedule_html ? (
              <div className="sede-linea">
                <span className="sede-label">Horarios:</span>
                <div className="horarios-list"
                  dangerouslySetInnerHTML={{ __html: s.schedule_html }} />
              </div>
            ) : (
              <div className="sede-linea text-muted">Horarios: No disponibles.</div>
            )}

            {(s.facebook_url || s.instagram_url || s.twitter_url) && (
              <div className="sede-linea d-flex gap-2 flex-wrap mt-1">
                {s.facebook_url && (
                  <a href={s.facebook_url} target="_blank" rel="noreferrer"
                    className="btn btn-outline-secondary btn-sm">Facebook</a>
                )}
                {s.instagram_url && (
                  <a href={s.instagram_url} target="_blank" rel="noreferrer"
                    className="btn btn-outline-secondary btn-sm">Instagram</a>
                )}
                {s.twitter_url && (
                  <a href={s.twitter_url} target="_blank" rel="noreferrer"
                    className="btn btn-outline-secondary btn-sm">X / Twitter</a>
                )}
              </div>
            )}
          </div>
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const Cooperativas = () => {
  const [coops, setCoops]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [selectedCoop, setSelectedCoop] = useState(null);

  useEffect(() => {
    const fetchCoops = async () => {
      try {
        const res  = await fetch(`${API_URL}/cooperatives`);
        const json = await res.json();
        if (json.ok && json.data.length > 0) {
          setCoops(json.data);
          setFilter(json.data[0].zone_name);
        }
      } catch (err) {
        console.error("Error cargando cooperativas:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCoops();
  }, []);

  // Zonas únicas en orden de aparición
  const zones = useMemo(() =>
    [...new Map(coops.map(c => [c.zone_name, c.zone_name])).keys()],
    [coops]
  );

  const filteredItems = coops.filter(c => c.zone_name === filter);

  const abrirModal  = (coop) => { setSelectedCoop(coop); setShowModal(true); };
  const cerrarModal = ()     => { setShowModal(false); setSelectedCoop(null); };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="text-center my-4">NUESTRAS COOPERATIVAS AFILIADAS</h2>

      {/* ── Botones de zona ──────────────────────────────────────────────── */}
      <ButtonGroup className="mb-4 d-flex justify-content-center flex-wrap">
        {zones.map((zone) => (
          <Button
            key={zone}
            onClick={() => setFilter(zone)}
            className="m-1"
            style={{
              backgroundColor: filter === zone ? "#1a1f42" : "#252A58",
              color: "white",
              border: "none",
              textAlign: "center",
              opacity: filter === zone ? 1 : 0.85,
            }}
          >
            {zone}
          </Button>
        ))}
      </ButtonGroup>

      {/* ── Cards ────────────────────────────────────────────────────────── */}
      <Row className="tight-spacing justify-content-center">
        {filteredItems.map((coop) => (
          <Col key={coop.id} md={4} sm={6} xs={12}>
            <Card
              className="gallery-card fixed-card coop-card-clickable h-100"
              role="button"
              tabIndex={0}
              onClick={() => abrirModal(coop)}
              onKeyDown={(e) => e.key === "Enter" && abrirModal(coop)}
              aria-label={`Ver información de ${coop.name}`}
            >
              <Card.Img
                variant="top"
                src={buildImgUrl(coop.logo_url) || buildImgUrl(coop.logo_path)}
                className="gallery-img"
                alt={coop.logo_alt || coop.name}
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <Card.Body className="gallery-body">
                <Card.Title className="gallery-title">{coop.name}</Card.Title>
              </Card.Body>
            </Card>
          </Col>
        ))}
        {filteredItems.length === 0 && (
          <p className="text-center text-muted py-4">
            No hay cooperativas registradas en esta zona.
          </p>
        )}
      </Row>

      {/* ── Modal ────────────────────────────────────────────────────────── */}
      <Modal
        show={showModal}
        onHide={cerrarModal}
        centered
        size="lg"
        dialogClassName="facach-modal"
        contentClassName="facach-modal-content"
        backdropClassName="facach-modal-backdrop"
      >
        <div
          className="facach-modal-hero"
          style={{
            "--hero-bg": selectedCoop?.logo_url
              ? `url(${buildImgUrl(selectedCoop.logo_url)})`
              : "none",
          }}
        >
          <div className="facach-modal-hero-overlay" />
          <div className="facach-modal-hero-content">
            <div className="facach-chip">Cooperativa</div>
            <h4 className="facach-modal-title">
              {selectedCoop?.name || "Información de la cooperativa"}
            </h4>
          </div>
          <button type="button" className="btn facach-close"
            aria-label="Cerrar" onClick={cerrarModal}>✕</button>
        </div>

        <div className="facach-modal-body">
          <p className="facach-parrafo muted">
            Información en actualización. Pronto agregaremos más detalles sobre esta cooperativa.
          </p>

          <div className="facach-divider"><span>SEDES Y HORARIOS</span></div>

          <RenderSedes branches={selectedCoop?.branches || []} />

          {selectedCoop?.website_url && (
            <>
              <div className="facach-divider soft"><span>ENLACES</span></div>
              <div className="d-flex gap-2 flex-wrap">
                <a href={selectedCoop.website_url} target="_blank" rel="noreferrer"
                  className="btn btn-outline-primary btn-sm">Sitio web</a>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Cooperativas;